import React from "react";
import { motion } from "framer-motion";
import {
    Brain,
    ShieldCheck,
    Target,
    BookOpen,
    Sparkles,
} from "lucide-react";

const CARDS = [
    {
        icon: Brain,
        title: "Traduz rótulos difíceis",
        desc: "Ingredientes técnicos viram explicações simples e fáceis de entender.",
    },
    {
        icon: ShieldCheck,
        title: "Considera seu perfil",
        desc: "A DentrIA analisa restrições, alergias e ingredientes que você prefere evitar.",
    },
    {
        icon: Target,
        title: "Entende sua meta",
        desc: "O app mostra se o produto ajuda ou atrapalha objetivos como emagrecer, ganhar massa ou cuidar da saúde.",
    },
    {
        icon: BookOpen,
        title: "Usa referências confiáveis",
        desc: "A DentrIA se apoia em bases públicas, diretrizes reconhecidas e informações técnicas de saúde, nutrição e segurança alimentar.",
    },
];

export default function DentrIA() {
    return (
        <section
            id="dentria"
            data-testid="dentria-section"
            className="relative py-20 md:py-32 bg-[#0B1C3A] overflow-hidden grain"
        >
            {/* Ambient tech blobs */}
            <div
                aria-hidden
                className="blob"
                style={{
                    width: 520,
                    height: 520,
                    top: -160,
                    left: -160,
                    background:
                        "radial-gradient(circle, rgba(22,163,74,0.32), transparent 65%)",
                }}
            />
            <div
                aria-hidden
                className="blob"
                style={{
                    width: 520,
                    height: 520,
                    bottom: -180,
                    right: -120,
                    background:
                        "radial-gradient(circle, rgba(37,99,235,0.28), transparent 65%)",
                }}
            />

            {/* Neural grid */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.08]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                    maskImage:
                        "radial-gradient(ellipse at center, black 40%, transparent 75%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse at center, black 40%, transparent 75%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <div
                        data-testid="dentria-eyebrow"
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 bg-white/5 text-white/80 text-xs font-bold tracking-widest uppercase backdrop-blur"
                    >
                        <Sparkles size={14} className="text-[#16A34A]" />
                        IA exclusiva · Dentro
                    </div>
                    <h2
                        data-testid="dentria-title"
                        className="mt-5 font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold tracking-tight text-white"
                    >
                        Conheça a{" "}
                        <span className="bg-gradient-to-r from-[#16A34A] via-[#2fd27a] to-[#2563EB] bg-clip-text text-transparent">
                            DentrIA
                        </span>
                        .
                    </h2>
                    <p
                        data-testid="dentria-subtitle"
                        className="mt-4 font-display text-xl sm:text-2xl font-semibold text-white/80"
                    >
                        A inteligência artificial exclusiva do Dentro.
                    </p>
                </motion.div>

                <div className="mt-12 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="lg:col-span-5 space-y-5 text-white/80 text-lg leading-relaxed"
                    >
                        <p>
                            A DentrIA é a inteligência por trás do Dentro. Ela
                            interpreta rótulos, ingredientes, nutrientes e
                            pontos de atenção para transformar informações
                            técnicas em respostas simples, visuais e
                            personalizadas.
                        </p>
                        <p>
                            A cada scan, a DentrIA cruza os dados do produto com
                            seu perfil, suas restrições e seus objetivos para
                            ajudar você a entender melhor o que está
                            consumindo.
                        </p>

                        <div
                            data-testid="dentria-highlight"
                            className="mt-6 rounded-3xl p-[1.5px] bg-gradient-to-r from-[#16A34A] via-[#2fd27a] to-[#2563EB]"
                        >
                            <div className="rounded-[22px] bg-[#0B1C3A] px-6 py-5">
                                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#16A34A]">
                                    Em destaque
                                </div>
                                <p className="mt-1 font-display text-xl sm:text-2xl font-extrabold text-white leading-snug">
                                    DentrIA: do rótulo à decisão, em segundos.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                        {CARDS.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <motion.div
                                    key={c.title}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{
                                        duration: 0.55,
                                        delay: i * 0.08,
                                    }}
                                    data-testid={`dentria-card-${i + 1}`}
                                    className="group relative rounded-3xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all"
                                >
                                    <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                                        style={{
                                            background:
                                                "radial-gradient(400px circle at 50% -20%, rgba(22,163,74,0.18), transparent 60%)",
                                        }}
                                    />
                                    <div className="relative">
                                        <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#16A34A] to-[#2563EB] grid place-items-center text-white shadow-[0_10px_24px_-10px_rgba(22,163,74,0.7)]">
                                            <Icon size={20} strokeWidth={2.25} />
                                        </div>
                                        <h3 className="mt-5 font-display text-xl font-extrabold text-white">
                                            {c.title}
                                        </h3>
                                        <p className="mt-2 text-[15px] text-white/70 leading-relaxed">
                                            {c.desc}
                                        </p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    data-testid="dentria-disclaimer"
                    className="mt-12 text-sm text-white/55 max-w-3xl"
                >
                    As análises da DentrIA são educativas e não substituem
                    orientação médica, nutricional ou profissional.
                </motion.p>
            </div>
        </section>
    );
}
